import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Button, Checkbox, IconButton, Paper, Tooltip } from "@mui/material";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { deleteDiagnosticoAccidente } from "../../../../redux/actions/denuncia";
import { tableConfig } from "../../../commons/table/tableConfig";
import { StyledNuevaDenuncia } from "../../../styled/Siniestros";
import DiagnosticoAccidenteDialog from "../forms/dialog/DiagnosticoAccidenteDialog";

const AccidenteTable = ({ data, handleSubmit }) => {
  const dispatch = useDispatch();

  const cantidad = useSelector(
    (state) => state.denuncia.postDenuncia.diagnosticoAccidente.length
  );
  const listaAccidente = useSelector(
    (state) => state.denuncia.postDenuncia.diagnosticoAccidente
  );

  const [hayPrincipal, setHayPrincipal] = useState(null);
  const [openAddDiagnostico, setOpenAddDiagnostico] = useState(false);
  const [disabledCheck, setDisabledCheck] = useState(false);
  const [onEdit, setOnEdit] = useState(false);
  const [diagnostico, setDiagnostico] = useState({});
  const [firstDiag, setFirstDiag] = useState(true);

  useEffect(() => {
    if (cantidad) {
      setFirstDiag(false);
    } else {
      setFirstDiag(true);
    }
  }, [cantidad]);

  useEffect(() => {
    setHayPrincipal(listaAccidente.find((e) => e.principal === true));
    if (!hayPrincipal) {
      setDisabledCheck(false);
    }
  }, [listaAccidente, openAddDiagnostico, hayPrincipal]);

  const deleteDiagnostico = (id) => {
    dispatch(deleteDiagnosticoAccidente(id));
  };

  const addDiagnostico = () => {
    setOnEdit(false);
    openDialogWithConditional();
  };

  const openDialogWithConditional = (diagnostico, onEdit) => {
    if (hayPrincipal && onEdit) {
      if (diagnostico?.id !== hayPrincipal.id) {
        setDisabledCheck(true);
      } else {
        setDisabledCheck(false);
      }
    } else if (hayPrincipal && !onEdit) {
      setDisabledCheck(true);
    }
    setOpenAddDiagnostico(true);
  };

  const editDiagnostico = (diagnostico) => {
    setDiagnostico(diagnostico);
    setOnEdit(true);
    openDialogWithConditional(diagnostico, true);
  };

  const handleSetHayPrincipal = (values) => {
    setHayPrincipal(values);
  };

  const handleClose = () => {
    setOpenAddDiagnostico(false);
  };

  const columns = useMemo(() => [
    {
      accessorKey: "descDiagnosticoOms",
      header: "Diagnóstico",
      size: 200,
    },
    {
      accessorKey: "descGravedad",
      header: "Gravedad",
    },
    {
      accessorKey: "descZonasAfectadas",
      header: "Zona Afectada",
    },
    {
      accessorKey: "descNaturalezaLesion",
      header: "Naturaleza",
    },
    {
      accessorKey: "principal",
      header: "Principal",
      Cell: ({ row }) => (
        <Checkbox checked={row.original.principal} disabled readOnly />
      ),
      size: 100,
    },
  ], []);

  const table = useMaterialReactTable({
    ...tableConfig,
    columns,
    data: data || [],
    enableRowActions: true,
    positionActionsColumn: "last",
    displayColumnDefOptions: {
      'mrt-row-actions': {
        size: 120,
        muiTableHeadCellProps: {
          align: "center",
          sx: {
            ...tableConfig.muiTableHeadCellProps?.sx,
            justifyContent: "start"
          }
        },
        muiTableBodyCellProps: {
          align: "center",
        }
      },
    },
    renderRowActions: ({ row }) => (
      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
        <Tooltip title="Editar" placement="bottom" arrow>
          <IconButton onClick={() => editDiagnostico(row.original)}>
            <StyledNuevaDenuncia.TableIcon component={EditIcon} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Eliminar" placement="bottom" arrow>
          <IconButton onClick={() => deleteDiagnostico(row.original.id)}>
            <StyledNuevaDenuncia.TableIcon component={DeleteIcon} />
          </IconButton>
        </Tooltip>
      </div>
    ),
    renderBottomToolbarCustomActions: () => (
      <StyledNuevaDenuncia.TableFooter>
        <StyledNuevaDenuncia.TableButtonContainer>
          <Button
            color="primary"
            onClick={addDiagnostico}
            disabled={cantidad === 3}
          >
            Agregar Diagnostico
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => handleSubmit(true)}
            disabled={!hayPrincipal && Boolean(cantidad)}
          >
            Guardar Formulario
          </Button>
        </StyledNuevaDenuncia.TableButtonContainer>
      </StyledNuevaDenuncia.TableFooter>
    ),
    muiTablePaperProps: {
      elevation: 1,
      sx: { mb: 2 },
    },
    enablePagination: true,
    paginationDisplayMode: 'pages',
    muiPaginationProps: {
      rowsPerPageOptions: [5, 10, 25],
      labelRowsPerPage: "Filas por página:",
      getItemAriaLabel: (type) => {
        if (type === 'first') return 'ir a la primera página';
        if (type === 'last') return 'ir a la última página';
        if (type === 'next') return 'ir a la página siguiente';
        return 'ir a la página anterior';
      },
      labelDisplayedRows: ({ from, to, count }) =>
        `${from}-${to} de ${count !== -1 ? count : to}`,
    },
    initialState: {
      pagination: {
        pageSize: 5,
        pageIndex: 0,
      },
      density: 'compact',
    },
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    localization: {
      ...MRT_Localization_ES,
      noRecordsToDisplay: "No hay diagnósticos agregados",
    },
  });

  return (
    <>
      <Paper>
        <MaterialReactTable table={table} />
      </Paper>
      <DiagnosticoAccidenteDialog
        open={openAddDiagnostico}
        onEdit={onEdit}
        diagnostico={diagnostico}
        handleClose={handleClose}
        setOnEditFalse={() => setOnEdit(false)}
        setHayPrincipal={handleSetHayPrincipal}
        disabledCheck={disabledCheck}
        firstDiag={firstDiag}
      />
    </>
  );
};

export default AccidenteTable;